import {Platform} from 'react-native';

export const RULE_SYSTEMS = [
  {id: 'dnd-5e-2014', label: 'D&D 5e (2014)'},
  {id: 'dnd-5e-2024', label: 'D&D 5e (2024)'},
  {id: 'dnd-3.5e', label: 'D&D 3.5e'},
  {id: 'adnd-2e', label: 'AD&D 2e'},
  {id: 'dnd-bx', label: 'D&D Basic/Expert (B/X)'},
  {id: 'pathfinder-2e-remaster', label: 'Pathfinder 2e Remaster'},
] as const;

export type RuleSystemId = typeof RULE_SYSTEMS[number]['id'];
export const DEFAULT_RULE_SYSTEM: RuleSystemId = 'pathfinder-2e-remaster';
export const RULE_SYSTEM_STORAGE_KEY = 'dnd-friend.rules-system';

const listeners = new Set<() => void>();

export function isRuleSystemId(value: unknown): value is RuleSystemId {
  return RULE_SYSTEMS.some(system => system.id === value);
}

function readSavedRules(): RuleSystemId {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const saved = window.localStorage.getItem(RULE_SYSTEM_STORAGE_KEY);
      if (isRuleSystemId(saved)) return saved;
    }
  } catch {
    // The selector still works when browser storage is unavailable.
  }
  return DEFAULT_RULE_SYSTEM;
}

let selectedRules = readSavedRules();

export function getSelectedRules(): RuleSystemId {
  return selectedRules;
}

function updateRules(value: RuleSystemId) {
  if (selectedRules === value) return;
  selectedRules = value;
  listeners.forEach(listener => listener());
}

export function setSelectedRules(value: RuleSystemId) {
  if (!isRuleSystemId(value)) return;
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.localStorage.setItem(RULE_SYSTEM_STORAGE_KEY, value);
    }
  } catch {
    // Keep the selection for this session even if it cannot be saved.
  }
  updateRules(value);
}

export function subscribeToRules(listener: () => void) {
  listeners.add(listener);
  const handleStorage = (event: StorageEvent) => {
    if (event.key === RULE_SYSTEM_STORAGE_KEY || event.key === null) {
      updateRules(readSavedRules());
    }
  };
  const browser = Platform.OS === 'web' && typeof window !== 'undefined' ? window : undefined;
  browser?.addEventListener('storage', handleStorage);
  return () => {
    listeners.delete(listener);
    browser?.removeEventListener('storage', handleStorage);
  };
}

export function prefixRulesPrompt(message: string): string {
  const system = RULE_SYSTEMS.find(system => system.id === getSelectedRules())!;
  return `using the ${system.label} rules: \n${message}`;
}
